import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { Command } from "cmdk";
import { selectAllJobs } from "../../redux/slices/jobsCacheSlice";
import axiosInstance from "../../interceptors";

// ── Command palette (prompt #5) ─────────────────────────────────────────────
// Globally mounted via AppShell. Opens on Cmd+K (macOS) or Ctrl+K (Win/Linux).
// Fuzzy-search across:
//   - Jump to page   (admin routes — static)
//   - Jump to entity (jobs from Redux cache + remote candidates/pools/jobs
//                     via GET /job-posting/quick-search)
//   - Actions        (verbs: "New job", "Invite candidate"…)
//   - Settings/Help
//
// Remote search fires on input >= 2 chars, debounced 180ms.
// Closes on Esc / backdrop click / item selection. cmdk handles arrow-key
// navigation and the listbox a11y semantics.

const REMOTE_DEBOUNCE_MS = 180;
const REMOTE_MIN_CHARS = 2;

const RECENT_KEY = "ample_command_recent_v1";
const MAX_RECENT = 8;

const PAGES = [
  { id: "p-dashboard", label: "Dashboard",        to: "/admin/dashboard-v2",  keywords: "home overview" },
  { id: "p-jobs",      label: "Jobs",             to: "/admin/jobs",          keywords: "openings positions roles" },
  { id: "p-pipeline",  label: "Pipeline",         to: "/admin/pipeline",      keywords: "funnel stages candidates" },
  { id: "p-interviews",label: "Interviews",       to: "/admin/interviews",    keywords: "live scored sessions" },
  { id: "p-compare",   label: "Compare",          to: "/admin/compare",       keywords: "side-by-side candidates" },
  { id: "p-talent",    label: "Talent pools",     to: "/admin/talent-pools",  keywords: "cohort sourcing" },
  { id: "p-source",    label: "Source hub",       to: "/admin/source-hub",    keywords: "sourcing channels boards" },
  { id: "p-analytics", label: "Analytics",        to: "/admin/analytics",     keywords: "dashboards reports goals" },
  { id: "p-approvals", label: "My approvals",     to: "/admin/approvals",     keywords: "offers signoff" },
  { id: "p-offers",    label: "Offers",           to: "/admin/offers",        keywords: "offer letters sent accepted" },
  { id: "p-career",    label: "Career pages",     to: "/admin/career-pages",  keywords: "branded landing apply" },
  { id: "p-rubrics",   label: "Rubrics",          to: "/admin/rubrics",       keywords: "scorecards templates" },
  { id: "p-team",      label: "Team members",     to: "/admin/team-members",  keywords: "users invite" },
  { id: "p-settings",  label: "Settings",         to: "/admin/settings",      keywords: "config preferences" },
  { id: "p-email",     label: "Email inbox",      to: "/admin/email-inbox",   keywords: "messages communications" },
  { id: "p-questions", label: "Question bank",    to: "/admin/question-bank", keywords: "interview questions" },
];

const ACTIONS = [
  { id: "a-new-job",       label: "New job",              to: "/admin/create-job",      keywords: "create role posting" },
  { id: "a-new-rubric",    label: "New rubric",           to: "/admin/rubrics",         keywords: "scorecard template" },
  { id: "a-invite",        label: "Invite candidate",     to: "/admin/invite-candidates", keywords: "send invite email" },
  { id: "a-upload",        label: "Upload resumes",       to: "/admin/upload-resumes",  keywords: "import cv batch" },
  { id: "a-career-new",    label: "New career page",      to: "/admin/career-pages/new", keywords: "branded landing apply" },
  { id: "a-approvals-chain",label: "New approval chain",  to: "/admin/approval-chains/new", keywords: "workflow signoff" },
  { id: "a-generate-jd",   label: "Generate JD with AI",  to: "/admin/generate-jd",     keywords: "ai job description" },
];

const HELP = [
  { id: "h-shortcuts", label: "Keyboard shortcuts",  action: "shortcuts",      keywords: "help hotkeys" },
  { id: "h-settings",  label: "Settings",            to: "/admin/settings",    keywords: "config preferences" },
  { id: "h-account",   label: "My account",          to: "/admin/my-account",  keywords: "profile" },
];

function readRecents() {
  try {
    const raw = localStorage.getItem(RECENT_KEY);
    if (!raw) return [];
    const arr = JSON.parse(raw);
    return Array.isArray(arr) ? arr : [];
  } catch { return []; }
}

function pushRecent(item) {
  try {
    const cur = readRecents().filter((r) => r.id !== item.id);
    const next = [item, ...cur].slice(0, MAX_RECENT);
    localStorage.setItem(RECENT_KEY, JSON.stringify(next));
  } catch {}
}

export default function CommandPalette({ open, onClose, onOpenShortcuts }) {
  const [search, setSearch] = useState("");
  const [remote, setRemote] = useState({ jobs: [], candidates: [], talentPools: [] });
  const [remoteLoading, setRemoteLoading] = useState(false);
  const debounceRef = useRef(null);
  const navigate = useNavigate();
  const allJobs = useSelector(selectAllJobs);

  useEffect(() => {
    if (open) {
      setSearch("");
      setRemote({ jobs: [], candidates: [], talentPools: [] });
    }
  }, [open]);

  // Debounced remote search. Cleared on each keystroke; only the final
  // pause fires the network call.
  useEffect(() => {
    if (!open) return;
    if (debounceRef.current) clearTimeout(debounceRef.current);
    const trimmed = search.trim();
    if (trimmed.length < REMOTE_MIN_CHARS) {
      setRemote({ jobs: [], candidates: [], talentPools: [] });
      setRemoteLoading(false);
      return;
    }
    debounceRef.current = setTimeout(() => {
      setRemoteLoading(true);
      axiosInstance
        .get(`/job-posting/quick-search?q=${encodeURIComponent(trimmed)}`)
        .then((r) => {
          if (r?.data?.success && r.data.results) {
            setRemote({
              jobs: r.data.results.jobs || [],
              candidates: r.data.results.candidates || [],
              talentPools: r.data.results.talentPools || [],
            });
          }
        })
        .catch(() => {
          // Silent failure — palette stays usable from cached jobs +
          // static lists. A toast here would be noisy on every dropped
          // keystroke.
        })
        .finally(() => setRemoteLoading(false));
    }, REMOTE_DEBOUNCE_MS);
    return () => debounceRef.current && clearTimeout(debounceRef.current);
  }, [search, open]);

  const recents = useMemo(() => (open ? readRecents() : []), [open]);

  // Local cache of jobs (always available from Redux). Remote jobs supplement
  // — they're searched server-side and may include jobs not in the local
  // cache. Dedupe by jobId so a job in both lists shows once.
  const jobEntities = useMemo(() => {
    const fromCache = (allJobs || []).slice(0, 25).map((j) => ({
      id: `job-${j.jobId}`,
      label: j.positionName || `Job #${j.jobId}`,
      sublabel: (j.locations && j.locations.join(", ")) || "",
      to: `/admin/pipeline/${j.jobId}`,
      type: "Job",
      keywords: j.positionName || "",
    }));
    const seen = new Set(fromCache.map((j) => j.id));
    const fromRemote = (remote.jobs || [])
      .filter((j) => !seen.has(`job-${j.id}`))
      .map((j) => ({
        id: `job-${j.id}`,
        label: j.label,
        sublabel: j.sublabel || "",
        to: j.to,
        type: "Job",
        // Force cmdk's filter to keep this row visible — the server
        // already matched on this query.
        keywords: search,
      }));
    return [...fromCache, ...fromRemote];
  }, [allJobs, remote.jobs, search]);

  const candidateEntities = useMemo(
    () =>
      (remote.candidates || []).map((c) => ({
        id: `cand-${c.id}`,
        label: c.label,
        sublabel: c.sublabel || "",
        to: c.to,
        type: "Candidate",
        keywords: search,
      })),
    [remote.candidates, search],
  );

  const poolEntities = useMemo(
    () =>
      (remote.talentPools || []).map((p) => ({
        id: `pool-${p.id}`,
        label: p.label,
        sublabel: p.sublabel || "",
        to: p.to,
        type: "Pool",
        keywords: search,
      })),
    [remote.talentPools, search],
  );

  const handleSelect = useCallback(
    (item) => {
      pushRecent({ id: item.id, label: item.label, to: item.to, type: item.type });
      onClose?.();
      if (item.action === "shortcuts") {
        // Defer so the palette close animation lands first.
        setTimeout(() => onOpenShortcuts?.(), 80);
        return;
      }
      if (item.to) navigate(item.to);
    },
    [navigate, onClose, onOpenShortcuts],
  );

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-toast flex items-start justify-center bg-slate-900/40 pt-[12vh] px-4 animate-fade-in"
      onClick={onClose}
      role="presentation"
    >
      {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions */}
      <div
        className="w-full max-w-xl bg-white rounded-card shadow-2xl border border-subtle overflow-hidden animate-scale-in"
        onClick={(e) => e.stopPropagation()}
      >
        <Command
          label="Command palette"
          shouldFilter
          className="flex flex-col"
        >
          <div className="flex items-center gap-2 px-4 py-3 border-b border-subtle">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="text-vf-muted flex-shrink-0" aria-hidden="true">
              <circle cx="11" cy="11" r="7" />
              <line x1="21" y1="21" x2="16.65" y2="16.65" />
            </svg>
            {/* autoFocus is intentional and standard for command palettes —
                the entire affordance is "open and start typing." Disabling
                it would surprise users. */}
            {/* eslint-disable-next-line jsx-a11y/no-autofocus */}
            <Command.Input
              value={search}
              onValueChange={setSearch}
              placeholder="What do you want to do?"
              className="flex-1 outline-none text-sm bg-transparent placeholder:text-vf-muted"
              autoFocus
            />
            <kbd className="text-2xs text-vf-muted border border-subtle rounded px-1.5 py-0.5 font-mono">
              esc
            </kbd>
          </div>

          <Command.List className="max-h-[60vh] overflow-y-auto py-2">
            <Command.Empty className="px-4 py-6 text-center text-xs text-vf-muted">
              No matches. Try a different keyword.
            </Command.Empty>

            {!search && recents.length > 0 && (
              <Command.Group heading="Recent" className="text-2xs uppercase tracking-wide text-vf-muted px-4 pt-2 pb-1">
                {recents.map((r) => (
                  <PaletteItem key={r.id} item={r} onSelect={handleSelect} typeBadge={r.type || "Page"} />
                ))}
              </Command.Group>
            )}

            <Command.Group heading="Jump to" className="text-2xs uppercase tracking-wide text-vf-muted px-4 pt-2 pb-1">
              {PAGES.map((p) => (
                <PaletteItem key={p.id} item={p} onSelect={handleSelect} typeBadge="Page" />
              ))}
            </Command.Group>

            {jobEntities.length > 0 && (
              <Command.Group heading="Jobs" className="text-2xs uppercase tracking-wide text-vf-muted px-4 pt-2 pb-1">
                {jobEntities.map((j) => (
                  <PaletteItem
                    key={j.id}
                    item={j}
                    onSelect={handleSelect}
                    typeBadge="Job"
                    sublabel={j.sublabel}
                  />
                ))}
              </Command.Group>
            )}

            {candidateEntities.length > 0 && (
              <Command.Group heading="Candidates" className="text-2xs uppercase tracking-wide text-vf-muted px-4 pt-2 pb-1">
                {candidateEntities.map((c) => (
                  <PaletteItem
                    key={c.id}
                    item={c}
                    onSelect={handleSelect}
                    typeBadge="Candidate"
                    sublabel={c.sublabel}
                  />
                ))}
              </Command.Group>
            )}

            {poolEntities.length > 0 && (
              <Command.Group heading="Talent pools" className="text-2xs uppercase tracking-wide text-vf-muted px-4 pt-2 pb-1">
                {poolEntities.map((p) => (
                  <PaletteItem
                    key={p.id}
                    item={p}
                    onSelect={handleSelect}
                    typeBadge="Pool"
                    sublabel={p.sublabel}
                  />
                ))}
              </Command.Group>
            )}

            {remoteLoading && (
              <div className="px-4 py-2 text-xs text-vf-muted italic" role="status" aria-live="polite">
                Searching…
              </div>
            )}

            <Command.Group heading="Actions" className="text-2xs uppercase tracking-wide text-vf-muted px-4 pt-2 pb-1">
              {ACTIONS.map((a) => (
                <PaletteItem key={a.id} item={a} onSelect={handleSelect} typeBadge="Action" />
              ))}
            </Command.Group>

            <Command.Group heading="Help & settings" className="text-2xs uppercase tracking-wide text-vf-muted px-4 pt-2 pb-1">
              {HELP.map((h) => (
                <PaletteItem key={h.id} item={h} onSelect={handleSelect} typeBadge="" />
              ))}
            </Command.Group>
          </Command.List>

          <footer className="border-t border-subtle px-4 py-2 flex items-center justify-between text-2xs text-vf-muted bg-vf-bg">
            <span>
              <kbd className="bg-white border border-subtle rounded px-1 py-0.5 font-mono mr-1">↑↓</kbd>
              navigate
              <kbd className="bg-white border border-subtle rounded px-1 py-0.5 font-mono mx-1">↵</kbd>
              select
            </span>
            <span>
              <kbd className="bg-white border border-subtle rounded px-1 py-0.5 font-mono">?</kbd>
              {" "}for keyboard shortcuts
            </span>
          </footer>
        </Command>
      </div>
    </div>
  );
}

function PaletteItem({ item, onSelect, typeBadge, sublabel }) {
  return (
    <Command.Item
      value={`${item.label} ${item.keywords || ""}`}
      onSelect={() => onSelect(item)}
      className="flex items-center gap-3 px-4 py-2 text-sm cursor-pointer aria-selected:bg-vf-tint aria-selected:text-vf-base text-vf-base rounded mx-2"
    >
      <div className="flex-1 min-w-0">
        <div className="truncate">{item.label}</div>
        {(sublabel || item.sublabel) && (
          <div className="text-2xs text-vf-muted truncate">{sublabel || item.sublabel}</div>
        )}
      </div>
      {typeBadge && (
        <span className="text-2xs text-vf-muted border border-subtle rounded px-1.5 py-0.5">
          {typeBadge}
        </span>
      )}
    </Command.Item>
  );
}
